/* Lifetime Gifts admin panel — extracted from Admin.jsx tech-debt split. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function LifetimeGiftsPanel() {
  const [gifts, setGifts] = useState([]);
  const [form, setForm] = useState({ recipient_email: "", recipient_name: "", personal_message: "" });
  const [sending, setSending] = useState(false);

  const reload = () => api.get("/admin/lifetime-gifts").then((r) => setGifts(r.data || []));
  useEffect(() => { reload(); }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!form.recipient_email.trim() || !form.recipient_name.trim()) {
      toast.error("Email and name are required");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/lifetime-gifts", form);
      if (data?.email?.sent) toast.success(`Gift email sent to ${form.recipient_email} ★`);
      else if (data?.email?.mock) toast.success("Gift created (email mocked — share link manually)");
      else toast.success("Gift created. Email blocked by Resend test domain — share the redeem link manually.");
      setForm({ recipient_email: "", recipient_name: "", personal_message: "" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create gift");
    } finally {
      setSending(false);
    }
  };

  const cancel = async (g) => {
    if (!window.confirm(`Cancel the lifetime gift for ${g.recipient_email}? They won't be able to redeem it.`)) return;
    try {
      await api.post(`/admin/lifetime-gifts/${g.gift_id}/cancel`);
      toast.success("Gift cancelled");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  const copyLink = (g) => {
    const url = `${window.location.origin}/redeem/${g.code}`;
    navigator.clipboard?.writeText(url);
    toast.success("Redeem link copied to clipboard");
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <form onSubmit={send} className="card-recipe" data-testid="gift-form">
        <p className="overline mb-2 text-rf-orange">★ Lifetime gift</p>
        <h2 className="font-display text-2xl font-black text-rf-ink mb-2">Gift a lifetime membership</h2>
        <p className="text-sm text-rf-ink-soft mb-5">The recipient gets an email with a unique link. They sign in or register, click Claim, and their account is bumped to lifetime — unlimited recipes &amp; cookbooks, forever, at no cost to them.</p>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="overline">Recipient name</label>
            <input data-testid="gift-recipient-name" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="input-rf mt-1" placeholder="Auntie Margaret" />
          </div>
          <div>
            <label className="overline">Recipient email</label>
            <input data-testid="gift-recipient-email" type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="input-rf mt-1" placeholder="aunt.margaret@example.com" />
          </div>
        </div>
        <div className="mb-4">
          <label className="overline">Personal message (optional)</label>
          <textarea data-testid="gift-personal-message" rows={3} value={form.personal_message} onChange={(e) => setForm({ ...form, personal_message: e.target.value })} className="input-rf mt-1" placeholder="Happy birthday Auntie — here's all your hand-written recipes in one place, our gift to you." />
        </div>
        <button data-testid="gift-send-btn" disabled={sending} className="btn-amber">{sending ? "Sending…" : "★ Send gift"}</button>
      </form>

      <div className="card-recipe overflow-x-auto" data-testid="gifts-table">
        <h3 className="font-display text-xl font-black text-rf-ink mb-4">Issued gifts ({gifts.length})</h3>
        {gifts.length === 0 ? (
          <p className="text-rf-ink-soft text-sm">No gifts yet. Send your first one above.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-rf-ink-soft border-b border-rf-ink/10">
                <th className="py-2">Recipient</th><th>Gifter</th><th>Status</th><th>Created</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {gifts.map((g) => (
                <tr key={g.gift_id} data-testid={`gift-row-${g.code}`} className="border-b border-rf-ink/5">
                  <td className="py-3">
                    <p className="font-bold text-rf-ink">{g.recipient_name}</p>
                    <p className="text-xs font-mono text-rf-ink-soft">{g.recipient_email}</p>
                  </td>
                  <td className="text-xs text-rf-ink-soft">{g.gifter_name}</td>
                  <td>
                    {g.redeemed
                      ? <span className="chip !bg-emerald-500/20 !text-emerald-800 font-bold">★ Claimed</span>
                      : g.cancelled
                      ? <span className="chip !bg-red-500/15 !text-red-800">Cancelled</span>
                      : <span className="chip !bg-rf-amber/30 !text-rf-ink">Pending</span>}
                  </td>
                  <td className="text-xs text-rf-ink-soft whitespace-nowrap">
                    {new Date(g.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1.5">
                      <button data-testid={`gift-copy-${g.code}`} onClick={() => copyLink(g)} className="text-xs px-2 py-1 rounded-lg bg-rf-cream-alt hover:bg-rf-amber/30">Copy link</button>
                      {!g.redeemed && !g.cancelled && (
                        <button data-testid={`gift-cancel-${g.code}`} onClick={() => cancel(g)} className="text-xs px-2 py-1 rounded-lg bg-red-500/10 text-red-800 hover:bg-red-500/20">Cancel</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
